import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Calculator, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, trackEvent } from "../../lib/api";
import { ISDE_PATH, ISDE_PREFILL_KEY, nextAnnounced, ruleFor, useIsdeRules } from "../../lib/isde";
import { EMPTY_ISDE, ISDE_STEPS, buildCheckPayload, toWizardPrefill, tripleSelected } from "./isdeOptions";
import { StepOppervlak, StepPlanning, StepWerk, StepWoning } from "./IsdeSteps";
import { IsdeResult } from "./IsdeResult";

const apiError = (err) => {
  const d = err?.response?.data?.detail;
  if (err?.response?.status === 429) return "Te veel berekeningen achter elkaar. Probeer het over een minuut opnieuw.";
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d[0]?.msg) return `Controleer uw invoer: ${d[0].msg.replace(/^Value error, /, "")}`;
  return "Berekenen is niet gelukt. Controleer uw verbinding en probeer het opnieuw.";
};

const Progress = ({ step }) => (
  <div className="mb-7" data-testid="isde-progress">
    <div className="flex items-center justify-between text-[12.5px] font-semibold">
      <span className="uppercase tracking-[0.14em] text-gold">Stap {step} van {ISDE_STEPS.length}</span>
      <span className="text-slate-500" data-testid="isde-step-label">{ISDE_STEPS[step - 1].short}</span>
    </div>
    <ol className="mt-3 grid grid-cols-5 gap-1.5" aria-hidden="true">
      {ISDE_STEPS.map((s, i) => <li key={s.short} className={`h-1.5 rounded-full transition-colors duration-300 ${i < step ? "bg-maas" : "bg-slate-200"}`} />)}
    </ol>
  </div>
);

export const IsdeCheck = ({ initial = {} }) => {
  const navigate = useNavigate();
  const topRef = useRef(null);
  const rules = useIsdeRules();
  const transition = nextAnnounced(rules)?.transitions?.[0];
  const [form, setForm] = useState(() => ({ ...EMPTY_ISDE, ...initial }));
  const [step, setStep] = useState(1);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  // Gemengd HR++/HR+++ alleen als de actieve regelversie met de RVO-totaalafronding rekent.
  const rvo = ruleFor(rules, "actief")?.rounding_method === "rvo_totaal";
  const mixed = rvo && tripleSelected(form);

  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    const el = topRef.current;
    if (el && el.getBoundingClientRect().top < 80) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [step]);

  const calculate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/isde/check", buildCheckPayload(form, mixed));
      setResult(data);
      setStep(5);
      trackEvent("isde_check_result", { status: data.status });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };
  const next = () => {
    trackEvent("isde_check_step", { step, step_name: ISDE_STEPS[step - 1].short });
    if (step === 4) calculate(); else setStep((s) => s + 1);
  };
  const toQuote = () => {
    sessionStorage.setItem(ISDE_PREFILL_KEY, JSON.stringify(toWizardPrefill(form, result)));
    trackEvent("isde_quote_click", { status: result.status });
    navigate("/offerte-aanvragen?bron=isde", { state: { from: ISDE_PATH } });
  };

  const StepBody = [StepWoning, StepWerk, StepOppervlak, StepPlanning][step - 1];
  return (
    <div ref={topRef} id="subsidiecheck-form" data-testid="isde-check" className="scroll-mt-28 rounded-3xl border border-slate-200 bg-white p-5 sm:p-8" style={{ boxShadow: "var(--shadow-2)" }}>
      <Progress step={step} />
      <h2 className="text-[22px] font-bold leading-tight text-ink sm:text-[26px]" data-testid="isde-step-title">{ISDE_STEPS[step - 1].title}</h2>
      {step < 5 && <p className="mt-2 text-[14.5px] text-slate-500">Alles is optioneel. Weet u iets niet? Sla het gerust over; dan laten we zien welke gegevens nog nodig zijn.</p>}
      {step === 5 && result ? (
        <div className="mt-6"><IsdeResult result={result} onEdit={() => setStep(1)} onQuote={toQuote} prefill={toWizardPrefill(form, result)} /></div>
      ) : (
        <form className="mt-6" noValidate onSubmit={(e) => { e.preventDefault(); next(); }}>
          <StepBody form={form} set={set} transition={transition} mixed={mixed} rvo={rvo} />
          <div className="mt-8 flex flex-col-reverse gap-3 border-t border-slate-100 pt-5 sm:flex-row sm:items-center sm:justify-between">
            {step > 1 ? (
              <button type="button" onClick={() => setStep((s) => s - 1)} disabled={busy} data-testid="isde-back"
                className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-xl px-3 text-[15px] font-semibold text-slate-600 hover:bg-slate-50 hover:text-ink disabled:opacity-40">
                <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Vorige
              </button>
            ) : <span className="hidden sm:block" />}
            <button type="submit" disabled={busy} data-testid={step === 4 ? "isde-calculate" : "isde-next"}
              className={`${step === 4 ? "btn-cta" : "btn-primary min-h-[52px]"} w-full whitespace-nowrap sm:w-auto disabled:cursor-not-allowed disabled:opacity-60`}>
              {step === 4 ? (busy ? <><Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> Bezig met berekenen…</> : <><Calculator className="h-5 w-5" aria-hidden="true" /> Bekijk mijn uitkomst</>)
                : <>Volgende <ArrowRight className="h-4 w-4" aria-hidden="true" /></>}
            </button>
          </div>
          <p className="mt-3 text-center text-[12.5px] text-slate-500 sm:text-right">Geen contactgegevens nodig · de berekening gebeurt op basis van de RVO-regels</p>
        </form>
      )}
    </div>
  );
};
